import * as XLSX from 'xlsx'

export type TExcelColumn = {
  header: string
  key: string
  width?: number
  format?: 'money' | 'number' | 'percent' | 'text'
}

/**
 * Exporta datos a un archivo Excel (.xlsx)
 * @param data Array de objetos con los datos
 * @param columns Definición de columnas con headers y keys
 * @param filename Nombre del archivo sin extensión
 * @param sheetName Nombre de la hoja (default: 'Informe')
 */
export function exportToExcel(
  data: any[],
  columns: TExcelColumn[],
  filename: string,
  sheetName: string = 'Informe'
) {
  const headers = columns.map(c => c.header)

  const rows = data.map(row =>
    columns.map(col => {
      const value = row[col.key]
      if (value === null || value === undefined) return ''
      if (col.format === 'money' || col.format === 'number') {
        return typeof value === 'number' ? value : Number(value) || 0
      }
      if (col.format === 'percent') {
        return typeof value === 'number' ? value : Number(value) || 0
      }
      return String(value)
    })
  )

  const wsData = [headers, ...rows]
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  ws['!cols'] = columns.map(col => ({
    wch: col.width || 18
  }))

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)

  XLSX.writeFile(wb, `${filename}.xlsx`)
}

/**
 * Exporta múltiples hojas a un archivo Excel (.xlsx)
 */
export function exportMultiSheetExcel(
  sheets: { name: string; data: any[]; columns: TExcelColumn[] }[],
  filename: string
) {
  const wb = XLSX.utils.book_new()

  sheets.forEach(sheet => {
    const headers = sheet.columns.map(c => c.header)
    const rows = sheet.data.map(row =>
      sheet.columns.map(col => {
        const value = row[col.key]
        if (value === null || value === undefined) return ''
        if (col.format === 'money' || col.format === 'number') {
          return typeof value === 'number' ? value : Number(value) || 0
        }
        return String(value)
      })
    )

    const wsData = [headers, ...rows]
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    ws['!cols'] = sheet.columns.map(col => ({ wch: col.width || 18 }))
    XLSX.utils.book_append_sheet(wb, ws, sheet.name)
  })

  XLSX.writeFile(wb, `${filename}.xlsx`)
}

/**
 * Descarga un archivo a partir de su contenido codificado en Base64.
 * Útil para descargas seguras provenientes de la respuesta JSON cifrada del servidor.
 */
export function downloadBase64File(
  base64Data: string,
  fileName: string,
  mimeType: string = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
) {
  const byteCharacters = atob(base64Data)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: mimeType })

  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
